/**
 * TransferOwnershipModal — sl-kqzl.3.3
 *
 * Admin-only modal to transfer credential ownership to another workspace
 * member. Two-step flow: pick member → confirm. POST
 * /api/workspace/:id/credentials/:id/transfer-ownership.
 */
import { notify, RowInput, Scrollable } from '@affine/component';
import { Modal } from '@affine/component/ui/modal';
import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { getMembersByWorkspaceIdQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { SearchIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as permStyles from '../permissions/styles.css';
import * as navStyles from '../shared/settings-navigation.css';
import * as styles from './index.css';
import type { WorkspaceCredentialInfo } from './use-workspace-credentials';

interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  permission: string;
  isGuest: boolean;
}

interface TransferOwnershipModalProps {
  credential: WorkspaceCredentialInfo;
  onClose: () => void;
  onTransfer: (credentialId: string, newOwnerId: string) => Promise<void>;
  isSaving: boolean;
}

export function TransferOwnershipModal({
  credential,
  onClose,
  onTransfer,
  isSaving,
}: TransferOwnershipModalProps) {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(WorkspaceServerService);
  const workspaceId = workspaceService.workspace.id;

  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<WorkspaceMember | null>(
    null
  );

  useEffect(() => {
    const ac = new AbortController();
    const loadMembers = async () => {
      if (!serverService.server) return;
      try {
        const result = await serverService.server.gql({
          query: getMembersByWorkspaceIdQuery,
          variables: { workspaceId, take: 100 },
        });
        if (ac.signal.aborted) return;
        const fetched = (result.workspace?.members ?? []) as WorkspaceMember[];
        // Workspace members only — exclude externals (permission null) and
        // guests (bead AC: "NICHT Guests, NICHT externe Users").
        setMembers(fetched.filter(m => !!m.permission && !m.isGuest));
      } catch (err) {
        if (ac.signal.aborted) return;
        notify.error({
          title: `Failed to load members: ${err instanceof Error ? err.message : 'Unknown error'}`,
        });
      } finally {
        if (!ac.signal.aborted) setIsLoading(false);
      }
    };
    void loadMembers();
    return () => ac.abort();
  }, [serverService.server, workspaceId]);

  const filteredMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m => {
      const name = (m.name ?? '').toLowerCase();
      const email = (m.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [members, searchQuery]);

  const handleConfirm = useCallback(async () => {
    if (!selectedMember) return;
    try {
      await onTransfer(credential.id, selectedMember.id);
      onClose();
    } catch {
      // Error already surfaced via notify in hook.
    }
  }, [selectedMember, onTransfer, credential.id, onClose]);

  const title = selectedMember
    ? (t[
        'com.streamlain.settings.workspace.credentials.transfer.confirm-title'
      ]?.() ?? 'Confirm transfer')
    : (t[
        'com.streamlain.settings.workspace.credentials.transfer.pick-title'
      ]?.() ?? 'Transfer ownership');

  return (
    <Modal
      open
      onOpenChange={v => {
        if (!v) onClose();
      }}
      width={480}
      title={`${title}: ${credential.name}`}
      contentOptions={{
        style: {
          padding: '0 24px 24px',
          maxHeight: '80vh',
          overflowY: 'auto',
        },
      }}
    >
      {selectedMember ? (
        <div>
          <p
            style={{
              fontSize: '14px',
              lineHeight: 1.5,
              margin: '8px 0 16px 0',
            }}
          >
            {t[
              'com.streamlain.settings.workspace.credentials.transfer.confirm-description'
            ]?.({
              newOwner: selectedMember.name || selectedMember.email,
            }) ??
              `Transfer "${credential.name}" to ${selectedMember.name || selectedMember.email}? The previous owner will lose direct access unless explicitly granted.`}
          </p>
          <div className={styles.modalActions}>
            <button
              className={styles.cancelButton}
              onClick={() => setSelectedMember(null)}
              disabled={isSaving}
            >
              {t['com.affine.button.back']?.() ?? 'Back'}
            </button>
            <button
              className={styles.saveButton}
              onClick={() => void handleConfirm()}
              disabled={isSaving}
              data-testid="credential-transfer-confirm"
            >
              {isSaving
                ? (t['com.affine.button.transferring']?.() ?? 'Transferring...')
                : (t[
                    'com.streamlain.settings.workspace.credentials.transfer.confirm-cta'
                  ]?.() ?? 'Transfer ownership')}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className={navStyles.searchSection}>
            <SearchIcon />
            <RowInput
              data-testid="credential-transfer-search"
              className={navStyles.searchInput}
              placeholder={
                t[
                  'com.streamlain.settings.workspace.permissions.search-members'
                ]?.() ?? 'Search members...'
              }
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
          <Scrollable.Root>
            <Scrollable.Viewport style={{ maxHeight: 320 }}>
              {isLoading ? (
                <div className={permStyles.loadingState}>
                  {t['com.affine.loading']?.() ?? 'Loading...'}
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className={permStyles.emptyState}>
                  {t[
                    'com.streamlain.settings.workspace.permissions.no-members-found'
                  ]?.() ?? 'No members found'}
                </div>
              ) : (
                filteredMembers.map(member => (
                  <button
                    key={member.id}
                    className={permStyles.listItem}
                    data-testid={`credential-transfer-member-${member.id}`}
                    onClick={() => setSelectedMember(member)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <CachedUserAvatar
                      size={24}
                      userId={member.id}
                      name={member.name || member.email}
                      url={member.avatarUrl}
                    />
                    <div className={permStyles.listItemContent}>
                      <div className={permStyles.listItemTitle}>
                        {member.name || member.email}
                      </div>
                      {member.name && (
                        <div className={permStyles.listItemSubtitle}>
                          {member.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </Scrollable.Viewport>
            <Scrollable.Scrollbar />
          </Scrollable.Root>
        </div>
      )}
    </Modal>
  );
}
